import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Printer, Church, Baby } from "lucide-react";
import { Reveal, SectionHead } from "@/lib/motion";
import jaredFamily from "@/assets/jared-family.jpg";

const OTHER_ROLES = [
  {
    icon: Printer,
    title: "3D printing extraordinaire",
    body: "Turns out high-quality prints on demand — dialing in each job to exact tolerances so every piece is built for its specific purpose, never generic. That instinct to make every build situation-specific is the same one that treats your tax return as its own unique case, not a template.",
  },
  {
    icon: Church,
    title: "Ordained minister",
    body: "Ordained and trusted to stand with people through the moments that matter most. It is a role built entirely on discretion, care and keeping your word — the exact standard your financial information is held to here.",
  },
  {
    icon: Baby,
    title: "Full-time dad",
    body: "A hands-on, full-time dad to his two boys. Fatherhood is a daily lesson in patience, accountability and showing up — including in July, long after filing season, when a notice lands and you need someone who answers.",
  },
];

const EXPERIENCE_AREAS = [
  "W-2 employees",
  "Families and dependents",
  "Self-employed taxpayers",
  "Rental properties",
  "Investments",
  "Retirement income",
  "Multi-state taxation",
  "Prior-year filings",
  "Business ownership",
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-20">
      <section className="border-b border-steel-border">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <SectionHead
            overline="About"
            title="Experience you can actually measure."
            copy="Credentials and counts matter more than adjectives. Here are ours."
          />
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="about-credentials">
              {[
                ["PTIN", "Preparer Tax Identification Number"],
                ["ERO", "Authorized Electronic Return Originator"],
                ["EFIN", "Electronic Filing Identification Number"],
              ].map(([code, desc]) => (
                <div
                  key={code}
                  data-testid={`credential-${code.toLowerCase()}`}
                  className="border border-steel-brass/50 bg-steel-surface px-6 py-4 shadow-[0_0_24px_rgba(168,85,247,0.12)]"
                >
                  <span className="neon-text font-display text-xl font-bold tracking-[0.15em] text-steel-brass">
                    {code}
                  </span>
                  <p className="mt-1 text-xs text-steel-secondary">{desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-12">
              <div className="border border-steel-brass/40 bg-steel-surface p-10 md:col-span-5 shadow-[0_0_44px_rgba(168,85,247,0.10)]" data-testid="about-stat-card">
                <span className="font-display text-7xl font-medium tracking-tighter text-steel-text sm:text-8xl">
                  1,300<span className="text-steel-brass">+</span>
                </span>
                <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-steel-brass">
                  Individual returns assisted
                </p>
                <p className="mt-6 text-sm leading-relaxed text-steel-secondary">
                  That number represents thousands of W-2s reconciled, deductions found, notices decoded and
                  April deadlines met — the pattern recognition that only comes from volume.
                </p>
              </div>
              <div className="border border-steel-border bg-steel-surface p-10 md:col-span-7">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-steel-muted">
                  What that experience covers
                </p>
                <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                  {EXPERIENCE_AREAS.map((a) => (
                    <div key={a} className="flex items-center gap-3 text-sm text-steel-secondary">
                      <Check className="h-4 w-4 shrink-0 text-steel-brass" />
                      {a}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-steel-border">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 py-24 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative overflow-hidden border border-steel-border" data-testid="about-photo">
                <img
                  src={jaredFamily}
                  alt="Jared Steele with his twin boys"
                  className="block w-full"
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-steel-bg/70 to-transparent" />
                <p className="absolute bottom-4 left-5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/90">
                  Jared, with the two best reasons to get it right
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <SectionHead overline="Who prepares your return?" title="Jared Steele — Founder & Tax Preparer" />
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-6 text-base leading-relaxed text-steel-secondary sm:text-lg">
                <p>
                  Every return at Steele Industries is prepared or reviewed by the same person — the one whose
                  name is on the door. Jared holds a current PTIN (federal preparer registration), is an
                  authorized ERO with an EFIN — returns are e-filed directly under his own IRS authorization —
                  and maintains annual continuing education in federal tax law.
                </p>
                <p>
                  We publish credentials precisely because precision is the point: you will never see inflated
                  titles here. What you will see is exactly who handles your information, what qualifies them,
                  and how to reach them — including in July, when a notice arrives and the seasonal shops have
                  gone dark.
                </p>
                <p>
                  That last part matters. The IRS itself suggests taxpayers consider whether a preparer will
                  still be available months after filing season. We will be.
                </p>
              </div>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/contact"
                  data-testid="about-contact-btn"
                  className="group flex items-center justify-center gap-2 bg-steel-accent px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-steel-accentHover"
                >
                  Contact Jared
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/standards"
                  data-testid="about-standards-btn"
                  className="flex items-center justify-center gap-2 border border-steel-border px-8 py-4 text-sm font-medium text-steel-text transition-colors duration-300 hover:bg-steel-surface"
                >
                  Read Our Standards
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-steel-border">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <SectionHead
            overline="Beyond the numbers"
            title="The same person, in every role."
            copy="Who you hire is who you get. Outside tax season, Jared wears a few other hats — and each one sharpens how your return is handled."
          />
          <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-3">
            {OTHER_ROLES.map((role, i) => (
              <Reveal key={role.title} delay={i * 0.08}>
                <div
                  data-testid={`about-role-${i}`}
                  className="flex h-full flex-col border border-steel-border bg-steel-surface p-8 transition-colors duration-300 hover:border-steel-brass/50"
                >
                  <div className="flex h-12 w-12 items-center justify-center border border-steel-brass/40 text-steel-brass">
                    <role.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-6 font-display text-xl font-medium text-steel-text">{role.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-steel-secondary">{role.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
